import { UploadOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Dropdown, Empty, Flex, List, Tooltip, Typography, UploadFile } from 'antd';
import { AxiosResponse } from 'axios';
import { Fragment, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  deleteLeaveTicketAttach,
  getLeaveTicketAttach,
  uploadLeaveTicketFile
} from 'src/apis/hr_ticket/leaveTicket.api';
import More from 'src/assets/icons/more2.png';
import Icon from 'src/components/v2/Icon';
import AttachmentUploadModal from 'src/components/v2/Upload/AttachmentUpload';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { useBasePath } from 'src/hooks/useBasePath';
import { ILeaveTicket, ILeaveTicketAttachment } from 'src/types/hr_ticket/leaveTicket.type';
import { showNotification } from 'src/utils/notification';
import { getContentType } from 'src/utils/utils';
const { Text } = Typography;
type FileParam = {
  fileUrl: string;
  fileName: string;
};
export default function LeaveAttachment() {
  const { leaveTicket, setLeaveTicket, loading } = useContext(HrTicketDetailContext);
  const { userInfo } = useContext(AppContext);
  const [isOpenUploadModal, setIsOpenUploadModal] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const basePath = useBasePath();
  const { id } = useParams();

  const getLeaveTicketAttachMutation = useMutation({
    mutationFn: (param: FileParam) => getLeaveTicketAttach(param.fileUrl, param.fileName)
  });

  const deleteLeaveTicketAttachMutation = useMutation({
    mutationFn: (id: number) => deleteLeaveTicketAttach(id)
  });

  const uploadLeaveTicketFileMutation = useMutation({
    mutationFn: (body: FormData) => uploadLeaveTicketFile(body as FormData, String(id))
  });

  const handleGetFile = (item: ILeaveTicketAttachment, type: string) => {
    const param = { fileUrl: item.filePath, fileName: item.title };
    getLeaveTicketAttachMutation.mutate(param, {
      onSuccess: (response) => {
        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: getContentType(item?.contentType)
          })
        );
        if (type === 'read') {
          window.open(url, '_blank');
        } else {
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', item.title);
          document.body.appendChild(link);
          link.click();
        }
      }
    });
  };

  const handleDeleteFile = (id: number) => {
    deleteLeaveTicketAttachMutation.mutate(id, {
      onSuccess: () => {
        const dataClone = { ...leaveTicket?.data };
        dataClone.leaveTicketAttaches = dataClone.leaveTicketAttaches?.filter((x) => x.id !== id);
        setLeaveTicket((prev) => ({ ...prev, data: dataClone } as AxiosResponse<ILeaveTicket, any>));
      }
    });
  };

  const handleSave = (fileList: UploadFile<File>[]) => {
    const formData = new FormData();
    fileList.length > 0 &&
      fileList.forEach(function (file) {
        formData.append('files', file.originFileObj as Blob);
      });
    return uploadLeaveTicketFileMutation.mutateAsync(formData, {
      onError: () => {
        alert('Lỗi ảnh');
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['leaveTicket', id]);
        showNotification('Lưu thành công!', 'success');
        setIsOpenUploadModal(false);
      }
    });
  };

  return (
    <Fragment>
      <Flex gap={10} vertical>
        <div>
          <Divider style={{ marginBlock: 0 }} orientation='left'>
            <Text strong>Tệp đính kèm</Text>
          </Divider>
        </div>
        <List
          className='leave-ticket-attaches'
          loading={loading}
          rowKey={(item) => item.id}
          bordered={false}
          split={false}
          locale={{
            emptyText: (
              <Empty
                image='https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg'
                imageStyle={{ height: 100 }}
                description={<Text type='secondary'>Không có tệp đính kèm</Text>}
              />
            )
          }}
          dataSource={leaveTicket?.data?.leaveTicketAttaches}
          renderItem={(item, index) => (
            <List.Item
              style={{ padding: 0 }}
              actions={[
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: '1',
                        label: <Text onClick={() => handleGetFile(item, 'download')}>Tải xuống</Text>
                      },
                      {
                        key: '2',
                        label: (
                          <Text
                            disabled={
                              !(leaveTicket?.data.createdById === userInfo?.id && leaveTicket?.data.isAvailableEdit)
                            }
                            type='danger'
                            onClick={() => handleDeleteFile(item?.id)}
                          >
                            Xóa tệp
                          </Text>
                        )
                      }
                    ]
                  }}
                  trigger={['hover']}
                  placement='top'
                >
                  <Button type='link' icon={<Icon url={More} size={15} />} />
                </Dropdown>
              ]}
            >
              <List.Item.Meta
                title={
                  <Tooltip title={item?.title} placement='left'>
                    <Typography.Text onClick={() => handleGetFile(item, 'read')}>{item?.title}</Typography.Text>
                  </Tooltip>
                }
              />
            </List.Item>
          )}
        />
        {basePath === urlPath.hrConfirmLeaveTicket.location && (
          <Button
            shape='default'
            type='link'
            size='middle'
            icon={<UploadOutlined />}
            onClick={() => setIsOpenUploadModal(true)}
          >
            Thêm đính kèm
          </Button>
        )}
      </Flex>
      {isOpenUploadModal && (
        <AttachmentUploadModal
          setIsOpen={setIsOpenUploadModal}
          isOpen={isOpenUploadModal}
          isLoading={uploadLeaveTicketFileMutation.isLoading}
          onSubmit={handleSave}
        />
      )}
    </Fragment>
  );
}
